import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { useTranslation } from "react-i18next";

const NEW_COMBINATION_ROW_ID = "__new_price_driver_combination__";

const normalizeIds = (ids) =>
  [...new Set((ids || []).filter(Boolean))].sort((left, right) =>
    left.localeCompare(right)
  );

const buildCombinationKey = (qualityAttributeValueIds) => {
  const normalizedIds = normalizeIds(qualityAttributeValueIds);
  return normalizedIds.length === 0 ? "__NULL__" : normalizedIds.join("|");
};

const buildSelectedValuesByAttribute = (combination, attributes, valueLookup) => {
  const selectedValuesByAttribute = {};

  (attributes || []).forEach((attribute) => {
    const matchingValueId = (combination?.qualityAttributeValueIds || []).find(
      (qualityAttributeValueId) =>
        valueLookup[qualityAttributeValueId]?.qualityAttributeId ===
        attribute.qualityAttributeId
    );

    selectedValuesByAttribute[attribute.qualityAttributeId] = matchingValueId || "";
  });

  return selectedValuesByAttribute;
};

const buildEditingRow = (combination, attributes, valueLookup) => ({
  combinationId: combination?.combinationId || NEW_COMBINATION_ROW_ID,
  marketId: combination?.marketId || "",
  isCore: !!combination?.isCore,
  isNew: !combination?.combinationId,
  qualityAttributeValueIds: combination?.qualityAttributeValueIds || [],
  selectedValuesByAttribute: buildSelectedValuesByAttribute(
    combination,
    attributes,
    valueLookup
  ),
});

const MarketPriceDriverCombinationsTable = ({
  allowEdit,
  attributes,
  canAddCombination,
  combinations,
  marketId,
  onAdd,
  onDelete,
  onToggleCore,
  onUpdate,
  valueLookup,
}) => {
  const { t } = useTranslation(["markets"]);
  const [editingRow, setEditingRow] = useState(null);
  const [attributeErrors, setAttributeErrors] = useState({});
  const [rowError, setRowError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const firstInputRef = useRef(null);

  useEffect(() => {
    if (!editingRow) {
      setAttributeErrors({});
      setRowError("");
      return;
    }

    if (firstInputRef.current && typeof firstInputRef.current.focus === "function") {
      firstInputRef.current.focus();
    }
  }, [editingRow]);

  const getValueLabel = useCallback(
    (qualityAttributeValueIds, qualityAttributeId) => {
      const selectedValue = (qualityAttributeValueIds || [])
        .map((qualityAttributeValueId) => valueLookup[qualityAttributeValueId])
        .find((value) => value?.qualityAttributeId === qualityAttributeId);

      return selectedValue?.value || "";
    },
    [valueLookup]
  );

  const getSummary = useCallback(
    (qualityAttributeValueIds) => {
      if (!qualityAttributeValueIds || qualityAttributeValueIds.length === 0) {
        return t("NullCombination");
      }

      return qualityAttributeValueIds
        .map((qualityAttributeValueId) => valueLookup[qualityAttributeValueId]?.value)
        .filter(Boolean)
        .join(" / ");
    },
    [t, valueLookup]
  );

  const buildPayload = useCallback(
    (row) => {
      const requiredQualityAttributeIds = (attributes || []).map(
        (attribute) => attribute.qualityAttributeId
      );
      const qualityAttributeValueIds = requiredQualityAttributeIds
        .map((qualityAttributeId) => row.selectedValuesByAttribute[qualityAttributeId])
        .filter(Boolean);

      return {
        combinationId: row.isNew ? undefined : row.combinationId,
        marketId: marketId,
        isCore: !!row.isCore,
        requiredQualityAttributeIds,
        qualityAttributeValueIds,
      };
    },
    [attributes, marketId]
  );

  const getEditingSummary = useCallback(
    (row) => {
      const selectedIds = (attributes || [])
        .map((attribute) => row.selectedValuesByAttribute[attribute.qualityAttributeId])
        .filter(Boolean);

      if (selectedIds.length === 0) {
        return (attributes || []).length === 0
          ? t("NullCombination")
          : t("SelectValue");
      }

      return getSummary(selectedIds);
    },
    [attributes, getSummary, t]
  );

  const validateRow = useCallback(
    (row) => {
      const nextAttributeErrors = {};

      (attributes || []).forEach((attribute) => {
        if (!row.selectedValuesByAttribute[attribute.qualityAttributeId]) {
          nextAttributeErrors[attribute.qualityAttributeId] = t(
            "SelectValueForEachPriceDriverAttribute"
          );
        }
      });

      return nextAttributeErrors;
    },
    [attributes, t]
  );

  const clearEditingState = useCallback(() => {
    setEditingRow(null);
    setAttributeErrors({});
    setRowError("");
    setIsSaving(false);
  }, []);

  const saveEditingRow = useCallback(async () => {
    if (!editingRow || isSaving) {
      return false;
    }

    const nextAttributeErrors = validateRow(editingRow);
    if (Object.keys(nextAttributeErrors).length > 0) {
      setAttributeErrors(nextAttributeErrors);
      setRowError("");
      return false;
    }

    const payload = buildPayload(editingRow);
    const existingCombination = editingRow.isNew
      ? null
      : (combinations || []).find(
          (combination) => combination.combinationId === editingRow.combinationId
        );

    const hasChanges =
      editingRow.isNew ||
      !existingCombination ||
      existingCombination.isCore !== payload.isCore ||
      buildCombinationKey(existingCombination.qualityAttributeValueIds) !==
        buildCombinationKey(payload.qualityAttributeValueIds);

    if (!hasChanges) {
      clearEditingState();
      return true;
    }

    setIsSaving(true);
    setRowError("");

    try {
      if (editingRow.isNew) {
        await onAdd(payload);
      } else {
        await onUpdate(payload);
      }

      clearEditingState();
      return true;
    } catch (error) {
      setRowError(String(error || ""));
      setIsSaving(false);
      return false;
    }
  }, [
    buildPayload,
    clearEditingState,
    combinations,
    editingRow,
    isSaving,
    onAdd,
    onUpdate,
    validateRow,
  ]);

  const handleAddRow = useCallback(() => {
    setEditingRow(buildEditingRow(null, attributes, valueLookup));
    setAttributeErrors({});
    setRowError("");
  }, [attributes, valueLookup]);

  const handleEditRow = useCallback(
    (combination) => {
      setEditingRow(buildEditingRow(combination, attributes, valueLookup));
      setAttributeErrors({});
      setRowError("");
    },
    [attributes, valueLookup]
  );

  const handleCancelEdit = useCallback(() => {
    clearEditingState();
  }, [clearEditingState]);

  const handleValueChange = useCallback((qualityAttributeId, qualityAttributeValueId) => {
    setEditingRow((currentRow) => {
      if (!currentRow) return currentRow;

      return {
        ...currentRow,
        selectedValuesByAttribute: {
          ...currentRow.selectedValuesByAttribute,
          [qualityAttributeId]: qualityAttributeValueId,
        },
      };
    });

    setAttributeErrors((currentErrors) => {
      const nextErrors = { ...currentErrors };
      delete nextErrors[qualityAttributeId];
      return nextErrors;
    });
    setRowError("");
  }, []);

  const handleCoreChange = useCallback((isCore) => {
    setEditingRow((currentRow) =>
      currentRow
        ? {
            ...currentRow,
            isCore,
          }
        : currentRow
    );
    setRowError("");
  }, []);

  const handleRowBlur = useCallback(
    async (event) => {
      if (!editingRow || isSaving) {
        return;
      }

      if (
        event.relatedTarget &&
        event.currentTarget.contains(event.relatedTarget)
      ) {
        return;
      }

      await saveEditingRow();
    },
    [editingRow, isSaving, saveEditingRow]
  );

  const handleRowKeyDown = useCallback(
    async (event) => {
      if (!editingRow) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        handleCancelEdit();
      }

      if (event.key === "Enter") {
        event.preventDefault();
        await saveEditingRow();
      }
    },
    [editingRow, handleCancelEdit, saveEditingRow]
  );

  const renderedRows = useMemo(() => {
    if (editingRow?.isNew) {
      return [editingRow, ...(combinations || [])];
    }

    return combinations || [];
  }, [combinations, editingRow]);

  const actionColumnCount = allowEdit ? 3 : 2;
  const totalColumnCount = (attributes || []).length + actionColumnCount;
  const canStartAddRow = allowEdit && canAddCombination && !editingRow;

  return (
    <div>
      {allowEdit && (
        <div className="mb-3">
          <Button onClick={handleAddRow} disabled={!canStartAddRow} size="sm">
            {t("AddPriceDriverCombination")}
          </Button>
        </div>
      )}

      <div className="table-responsive">
        <table className="table table-sm table-hover table-bold align-middle">
          <thead>
            <tr>
              <th>{t("Combination")}</th>
              {(attributes || []).map((attribute) => (
                <th key={attribute.qualityAttributeId}>
                  {attribute.qualityAttributeName}
                </th>
              ))}
              <th>{t("CoreCombination")}</th>
              {allowEdit && <th>{t("Actions")}</th>}
            </tr>
          </thead>
          <tbody>
            {renderedRows.length === 0 && (
              <tr>
                <td colSpan={totalColumnCount} className="text-center text-muted py-3">
                  {t("NoPriceDriverCombinations")}
                </td>
              </tr>
            )}

            {renderedRows.map((row) => {
              const isEditingRow = editingRow?.combinationId === row.combinationId;
              const rowValues = isEditingRow ? editingRow : row;

              return (
                <tr
                  key={row.combinationId}
                  className={isEditingRow ? "table-active" : ""}
                  onBlur={isEditingRow ? handleRowBlur : undefined}
                  onKeyDown={isEditingRow ? handleRowKeyDown : undefined}
                >
                  <td className="font-weight-bold">
                    {isEditingRow ? (
                      <span className="text-muted">{getEditingSummary(rowValues)}</span>
                    ) : (
                      getSummary(row.qualityAttributeValueIds)
                    )}
                  </td>

                  {(attributes || []).map((attribute, index) => {
                    const selectedValue =
                      rowValues?.selectedValuesByAttribute?.[
                        attribute.qualityAttributeId
                      ] || "";
                    const error = attributeErrors[attribute.qualityAttributeId];

                    return (
                      <td key={attribute.qualityAttributeId}>
                        {isEditingRow ? (
                          <>
                            <Form.Select
                              ref={index === 0 ? firstInputRef : undefined}
                              size="sm"
                              value={selectedValue}
                              disabled={isSaving}
                              className={error ? "is-invalid" : ""}
                              onChange={(event) =>
                                handleValueChange(
                                  attribute.qualityAttributeId,
                                  event.target.value
                                )
                              }
                            >
                              <option value="">{t("SelectValue")}</option>
                              {(attribute.values || []).map((value) => (
                                <option
                                  key={value.qualityAttributeValueId}
                                  value={value.qualityAttributeValueId}
                                >
                                  {value.value}
                                </option>
                              ))}
                            </Form.Select>
                            {error && (
                              <div className="invalid-feedback d-block">{error}</div>
                            )}
                          </>
                        ) : (
                          <span>
                            {getValueLabel(
                              row.qualityAttributeValueIds,
                              attribute.qualityAttributeId
                            )}
                          </span>
                        )}
                      </td>
                    );
                  })}

                  <td>
                    {isEditingRow ? (
                      <Form.Check
                        type="checkbox"
                        checked={!!rowValues.isCore}
                        disabled={isSaving}
                        onChange={(event) => handleCoreChange(event.target.checked)}
                      />
                    ) : allowEdit ? (
                      <Form.Check
                        type="checkbox"
                        checked={!!row.isCore}
                        disabled={!!editingRow}
                        onChange={(event) =>
                          onToggleCore(row, event.target.checked)
                        }
                      />
                    ) : (
                      <Form.Check type="checkbox" checked={!!row.isCore} disabled />
                    )}
                  </td>

                  {allowEdit && (
                    <td style={{ minWidth: "220px" }}>
                      {isEditingRow ? (
                        <>
                          <div className="d-flex align-items-start">
                            <Button
                              size="sm"
                              className="mr-2"
                              onClick={saveEditingRow}
                              disabled={isSaving}
                            >
                              {t("Save")}
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              className="mr-2"
                              onClick={handleCancelEdit}
                              disabled={isSaving}
                            >
                              {t("Cancel")}
                            </Button>
                            {!rowValues.isNew && (
                              <button
                                type="button"
                                className="btn btn-sm btn-delete"
                                disabled={isSaving}
                                onClick={() => {
                                  clearEditingState();
                                  onDelete(row);
                                }}
                              >
                                {t("Delete")}
                              </button>
                            )}
                          </div>
                          {rowError && (
                            <div className="invalid-feedback d-block mt-2">
                              {rowError}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="d-flex align-items-start">
                          <Button
                            size="sm"
                            variant="primary"
                            className="mr-2"
                            onClick={() => handleEditRow(row)}
                            disabled={!!editingRow}
                          >
                            {t("Edit")}
                          </Button>
                          <button
                            type="button"
                            className="btn btn-sm btn-delete"
                            onClick={() => onDelete(row)}
                            disabled={!!editingRow}
                          >
                            {t("Delete")}
                          </button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MarketPriceDriverCombinationsTable;
